import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createCreation,
  uploadArtifact,
  finalizeInput,
  dciError,
  INPUT_METHOD,
} from "../../Services/dialogueCreations";
import { CaptureHeader, CapturePage, useFocusedCaptureHeader, makeIdempotencyKey } from "./shared";
import { PHOTO_COPY, errorText } from "./copy";
import imageIcon from "../../Assets/icon/capture/image.svg";
import closeIcon from "../../Assets/icon/capture/close.svg";

/** Photo creations take JPEG/PNG/HEIC, ≤ 10 per creation (guide §3.3). */
const ACCEPT = "image/jpeg,image/png,image/heic,image/heif";
const MAX_PHOTOS = 10;

/**
 * Upload a Photo (Figma 9856:14723 → 9856:14873). "Take Photo" asks the
 * browser for its camera (the phone's camera app on mobile; a file picker
 * where there is none); "Choose from Library" opens the photo picker. Photos
 * are previewed before anything is sent, then one PHOTO creation uploads them
 * all and finalizes.
 */
function CapturePhoto() {
  const navigate = useNavigate();
  useFocusedCaptureHeader();
  const cameraInput = useRef(null);
  const libraryInput = useRef(null);
  const key = useRef(makeIdempotencyKey("photo"));
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // "Take new photo" (one photo on screen) replaces it; "Add another" appends.
  const replaceNext = useRef(false);

  const previews = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const add = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    setError(null);
    const replace = replaceNext.current;
    replaceNext.current = false;
    setPhotos((prev) => {
      const next = replace ? incoming : [...prev, ...incoming];
      if (next.length > MAX_PHOTOS) setError(PHOTO_COPY.max);
      return next.slice(0, MAX_PHOTOS);
    });
  };

  const submit = async () => {
    if (photos.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const creation = await createCreation(INPUT_METHOD.PHOTO, key.current);
      const id = creation?.creation_request_id;
      for (const photo of photos) {
        // Sequential on purpose: the per-creation cap is checked per upload.
        // eslint-disable-next-line no-await-in-loop
        await uploadArtifact(id, photo);
      }
      await finalizeInput(id);
      navigate(`/capture/${encodeURIComponent(id)}`, { replace: true });
    } catch (e) {
      setError(errorText(dciError(e).code));
      setBusy(false);
    }
  };

  const pickers = (
    <>
      <input
        ref={cameraInput}
        type="file"
        accept={ACCEPT}
        capture="environment"
        hidden
        onChange={(e) => {
          add(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={libraryInput}
        type="file"
        accept={ACCEPT}
        multiple
        hidden
        onChange={(e) => {
          add(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );

  return (
    <CapturePage focused>
      <CaptureHeader title={PHOTO_COPY.title} />
      <div className="cap-body">
        {pickers}
        {photos.length === 0 ? (
          <>
            <div className="cap-card cap-photo-empty">
              <span className="cap-tile cap-tile--crayola">
                <img src={imageIcon} alt="" width="24" height="24" />
              </span>
              <span className="cap-drop__sub">{PHOTO_COPY.prompt}</span>
            </div>
            {error && <p className="cap-error">{error}</p>}
            <div className="cap-actions">
              <button type="button" className="cap-btn" onClick={() => cameraInput.current?.click()}>
                {PHOTO_COPY.take}
              </button>
              <button
                type="button"
                className="cap-btn cap-btn--outline"
                onClick={() => libraryInput.current?.click()}
              >
                {PHOTO_COPY.library}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={`cap-photos${photos.length === 1 ? " cap-photos--single" : ""}`}>
              {previews.map((src, i) => (
                <figure key={src} className="cap-photo">
                  <img src={src} alt="" />
                  {!busy && (
                    <button
                      type="button"
                      className="cap-photo__remove"
                      aria-label={PHOTO_COPY.remove}
                      onClick={() => setPhotos((prev) => prev.filter((_, j) => j !== i))}
                    >
                      <img src={closeIcon} alt="" width="16" height="16" />
                    </button>
                  )}
                </figure>
              ))}
            </div>
            {error && <p className="cap-error">{error}</p>}
            <div className="cap-actions">
              <button type="button" className="cap-btn" disabled={busy} onClick={submit}>
                {photos.length === 1 ? PHOTO_COPY.continue : PHOTO_COPY.continueMany(photos.length)}
              </button>
              {photos.length < MAX_PHOTOS && (
                <button
                  type="button"
                  className="cap-btn cap-btn--outline"
                  disabled={busy}
                  onClick={() => {
                    replaceNext.current = photos.length === 1;
                    cameraInput.current?.click();
                  }}
                >
                  {photos.length === 1 ? PHOTO_COPY.retake : PHOTO_COPY.addAnother}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </CapturePage>
  );
}

export default CapturePhoto;
